import {
  Box,
  Heading,
  Button,
  Badge,
  Code,
  Text,
  HStack,
  VStack,
  useColorModeValue,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Textarea,
  Stack,
  Alert,
  AlertIcon,
  Divider,
  SimpleGrid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
} from '@chakra-ui/react'
import { useState } from 'react'
import { resolvePlanChangeExpiry } from './planChangeExpiry'
import { useTranslation } from 'react-i18next'
import {
  FiRefreshCw,
  FiEye,
  FiEdit,
  FiShield,
  FiToggleLeft,
  FiInfo,
  FiCreditCard,
  FiSliders,
  FiClock,
} from 'react-icons/fi'
import {
  useUser,
  useUserLoginHistory,
  useUsers,
  useUpdatePlatformUser,
  useUpdatePlatformUserStatus,
  useRefreshUserMerchantOrderDefaults,
  useRecomputeUserMerchantSnapshots,
  useChangePlatformUserPlan,
} from '@/hooks/useAdminData'
import { useSubscriptionPlans } from '@/hooks/useSystemConfigData'
import { useToast } from '@/hooks/useToast'
import { DataTable } from '@/components/common/DataTable'
import { ColumnConfig, FilterConfig, PaginationConfig, ActionButton } from '@/components/common/DataTable/types'
import { StatusBadge } from '@/components/common/StatusBadge'
import { useCopyableCodeStyles } from '@/components/common/useCopyableCodeStyles'
import { formatDate, formatDateTime, formatTime } from '@/utils/formatUtils'
import { IdLookupPanel } from '@/components/common/IdLookupPanel'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import { PlatformUserMfaResetModal } from '@/components/users/PlatformUserMfaResetModal'
import type { AdminUserOrderDefaultsRefreshResult } from '@/types'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { triggerBrowserDownload } from '@/services/admin/baseService'
import { userService } from '@/services/admin/userService'

export function UsersPage() {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const [detailTabIndex, setDetailTabIndex] = useState(0)
  const [loginHistoryPage, setLoginHistoryPage] = useState(1)
  const [loginHistoryLimit, setLoginHistoryLimit] = useState(20)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [selectedUser, setSelectedUser] = useState<any | null>(null)
  const [editUser, setEditUser] = useState<any | null>(null)
  const [editForm, setEditForm] = useState({ 
    email: '',
    username: '',
    is_email_verified: false,
  })
  const [statusUser, setStatusUser] = useState<any | null>(null)
  const [mfaResetUser, setMfaResetUser] = useState<any | null>(null)

  const { data, isLoading, error, refetch, isFetching } = useUsers({
    page,
    limit,
    search,
    status,
  })
  const {
    data: selectedUserDetail,
    isLoading: isSelectedUserDetailLoading,
    isFetching: isSelectedUserDetailFetching,
    refetch: refetchSelectedUserDetail,
  } = useUser(selectedUser?.id ?? '')
  const {
    data: loginHistoryData,
    isLoading: isLoginHistoryLoading,
    isFetching: isLoginHistoryFetching,
    error: loginHistoryError,
    refetch: refetchLoginHistory,
  } = useUserLoginHistory(
    selectedUser?.id ?? '',
    { page: loginHistoryPage, limit: loginHistoryLimit },
    { enabled: Boolean(selectedUser?.id) && detailTabIndex === 3 },
  )
  const updatePlatformUserMutation = useUpdatePlatformUser()
  const updatePlatformUserStatusMutation = useUpdatePlatformUserStatus()
  const refreshUserOrderDefaultsMutation = useRefreshUserMerchantOrderDefaults()
  const recomputeUserSnapshotsMutation = useRecomputeUserMerchantSnapshots()
  const changePlanMutation = useChangePlatformUserPlan()
  const viewDisclosure = useDisclosure()
  const editDisclosure = useDisclosure()
  const statusDisclosure = useDisclosure()
  const mfaResetDisclosure = useDisclosure()
  const changePlanDisclosure = useDisclosure()
  const { data: subscriptionPlansData } = useSubscriptionPlans({
    enabled: changePlanDisclosure.isOpen,
  })
  const toast = useToast()
  const [userOrderDefaultsRefreshResult, setUserOrderDefaultsRefreshResult] =
    useState<AdminUserOrderDefaultsRefreshResult | null>(null)
  const [changePlanResult, setChangePlanResult] = useState<{
    merchantCount: number
    rebuiltCount: number
    failedMerchantIds: string[]
  } | null>(null)
  const [changePlanForm, setChangePlanForm] = useState({
    planId: '',
    endMode: 'date',
    endsAt: '',
    durationDays: '30',
    reason: '',
  })
  
  const textColor = useColorModeValue('gray.600', 'gray.300')
  const tablePrimaryTextColor = useColorModeValue('gray.700', 'gray.100')
  const copyableCodeStyles = useCopyableCodeStyles()
  const displayedUser = selectedUserDetail ?? selectedUser
  const isUserSnapshotOperationPending =
    refreshUserOrderDefaultsMutation.isPending || recomputeUserSnapshotsMutation.isPending
  const subscriptionPlans = Array.isArray((subscriptionPlansData as any)?.plans)
    ? (subscriptionPlansData as any).plans
    : Array.isArray(subscriptionPlansData)
      ? subscriptionPlansData
      : []
  const changePlanSelectOptions = subscriptionPlans.map((plan: any) => ({
    value: String(plan.id ?? ''),
    label: String(plan.displayName ?? plan.display_name ?? plan.name ?? plan.id ?? ''),
  }))
  const changePlanEndModeOptions = [
    { value: 'date', label: t('users.planChange.endAt', 'End at date/time') },
    { value: 'days', label: t('users.planChange.durationDays', 'Duration days') },
  ]

  const getStatusText = (isActive: boolean | undefined) => {
    if (isActive === undefined || isActive === null) return t('common.pending')
    return isActive ? t('users.status.active') : t('users.status.inactive')
  }

  const copyToClipboard = async (value: string, label: string) => {
    if (!value) return

    try {
      await navigator.clipboard.writeText(value)
      toast.success({
        title: t('common.copySuccess', 'Copied'),
        description: `${label} copied`,
      })
    } catch {
      toast.error({
        title: t('common.copyFailed', 'Copy failed'),
        description: `Unable to copy ${label.toLowerCase()}`,
      })
    }
  }

  const handleViewUser = (user: any) => {
    setSelectedUser(user)
    setDetailTabIndex(0)
    setUserOrderDefaultsRefreshResult(null)
    setLoginHistoryPage(1)
    setLoginHistoryLimit(20)
    viewDisclosure.onOpen()
  }

  const handleEditUser = (user: any) => {
    setEditUser(user)
    setEditForm({
      email: user.email ?? '',
      username: user.username ?? '',
      is_email_verified: Boolean(user.is_email_verified),
    })
    editDisclosure.onOpen()
  }

  const handleResetMfa = (user: any) => {
    setMfaResetUser(user)
    mfaResetDisclosure.onOpen()
  }

  const handleToggleStatus = async (user: any) => {
    if (!user) return
    if (user.is_active) {
      setStatusUser(user)
      statusDisclosure.onOpen()
      return
    }
    await confirmStatusChange(user, true)
  }

  const handleRefreshUserOrderDefaults = async () => {
    if (!selectedUser?.id) return
    try {
      const result = await refreshUserOrderDefaultsMutation.mutateAsync({
        userId: selectedUser.id,
      })
      setUserOrderDefaultsRefreshResult(result)
      toast.success({
        title: t('users.orderDefaults.refreshSuccessTitle'),
        description: t('users.orderDefaults.refreshSuccessDescription'),
      })
    } catch (err) {
      const responseData = (err as any)?.response?.data
      const errorMessage =
        responseData?.error?.message ??
        responseData?.message ??
        (err instanceof Error ? err.message : t('common.operationFailed'))

      toast.error({
        title: t('users.orderDefaults.refreshFailedTitle'),
        description: errorMessage,
      })
    }
  }

  const handleRecomputeUserSnapshots = async () => {
    if (!selectedUser?.id) return
    try {
      const result = await recomputeUserSnapshotsMutation.mutateAsync({
        userId: selectedUser.id,
      })
      setUserOrderDefaultsRefreshResult(result)
      toast.success({
        title: t('users.orderDefaults.recomputeSuccessTitle'),
        description: t('users.orderDefaults.recomputeSuccessDescription'),
      })
    } catch (err) {
      const responseData = (err as any)?.response?.data
      const errorMessage =
        responseData?.error?.message ??
        responseData?.message ??
        (err instanceof Error ? err.message : t('common.operationFailed'))

      toast.error({
        title: t('users.orderDefaults.recomputeFailedTitle'),
        description: errorMessage,
      })
    }
  }

  const handleRefreshCurrentDetailTab = async () => {
    if (!selectedUser?.id) {
      return
    }

    try {
      if (detailTabIndex === 3) {
        await refetchLoginHistory()
      } else {
        await refetchSelectedUserDetail()
      }
    } catch (err) {
      toast.error({
        title: t('common.error', 'Error'),
        description:
          err instanceof Error ? err.message : t('common.operationFailed'),
      })
    }
  }

  const isCurrentDetailTabRefreshing = detailTabIndex === 3
    ? isLoginHistoryFetching
    : isSelectedUserDetailFetching

  const openChangePlanModal = () => {
    setChangePlanForm({
      planId: displayedUser?.currentPlan?.id ?? '',
      endMode: 'date',
      endsAt: '',
      durationDays: '30',
      reason: '',
    })
    changePlanDisclosure.onOpen()
  }

  const handleChangePlanSubmit = async () => {
    if (!displayedUser?.id || !changePlanForm.planId || !changePlanForm.reason.trim()) {
      toast.error({
        title: t('common.error', 'Error'),
        description: t('users.planChange.required', 'Plan and reason are required'),
      })
      return
    }

    const selectedPlan = subscriptionPlans.find((plan: any) => plan.id === changePlanForm.planId)
    const planName = String(selectedPlan?.name ?? '').toLowerCase()
    let endsAt: string | null = null
    if (planName !== 'free') {
      endsAt = resolvePlanChangeExpiry(changePlanForm)
      if (!endsAt) {
        toast.error({
          title: t('common.error', 'Error'),
          description: t('users.planChange.invalidEndsAt'),
        })
        return
      }
    }

    const result = await changePlanMutation.mutateAsync({
      userId: displayedUser.id,
      data: {
        planId: changePlanForm.planId,
        endsAt,
        reason: changePlanForm.reason.trim(),
      },
    })
    setChangePlanResult(result.snapshotRebuild)
    changePlanDisclosure.onClose()
    toast.success({
      title: t('common.success', 'Success'),
      description: t('users.planChange.success', 'Plan updated'),
    })
  }

  const submitEdit = async () => {
    if (!editUser) return
    try {
      await updatePlatformUserMutation.mutateAsync({
        userId: editUser.id,
        data: editForm,
      })
      toast.success({
        titleKey: 'common.success',
        descriptionKey: 'users.userUpdated',
        description: t('users.userUpdated', 'User updated'),
      })
      editDisclosure.onClose()
      refetch()
    } catch (err) {
      toast.error({
        titleKey: 'common.error',
        descriptionKey: 'users.failedToUpdate',
        description: t('users.failedToUpdate', 'Update failed'),
      })
    }
  }

  const confirmStatusChange = async (user: any, enable?: boolean) => {
    if (!user) return
    const nextActive = enable ?? !user.is_active
    try {
      await updatePlatformUserStatusMutation.mutateAsync({
        userId: user.id,
        data: { is_active: nextActive },
      })
      toast.success({
        titleKey: 'common.success',
        description: nextActive
          ? t('users.enabled', 'User enabled')
          : t('users.disabled', 'User disabled'),
      })
      statusDisclosure.onClose()
      setStatusUser(null)
      refetch()
    } catch (err) {
      toast.error({
        titleKey: 'common.error',
        descriptionKey: 'users.failedToUpdate',
        description: t('users.failedToUpdate', 'Update failed'),
      })
    }
  }

  // Define column configuration for DataTable
  const columns: ColumnConfig<any>[] = [
    {
      key: 'userBusinessId',
      title: t('users.table.userId'),
      width: '18%',
      render: (_value, row) => {
        const businessId = row.userBusinessId || row.user_business_id || row.id
        return (
          <Tooltip label={businessId ? t('common.copy') : ''}>
            <Box minH="24px" display="flex" alignItems="center" justifyContent="flex-start">
              <Code
                as="div"
                fontSize="xs"
                bg="transparent"
                color={tablePrimaryTextColor}
                whiteSpace="normal"
                wordBreak="break-all"
                noOfLines={3}
                cursor={businessId ? 'pointer' : 'default'}
                _hover={businessId ? copyableCodeStyles.hover : undefined}
                _active={businessId ? copyableCodeStyles.active : undefined}
                onClick={() => businessId && copyToClipboard(businessId, 'User ID')}
              >
                {businessId || t('common.pending')}
              </Code>
            </Box>
          </Tooltip>
        )
      },
    },
    {
      key: 'username',
      title: t('users.table.username'),
      width: '18%',
      render: (value, row) => {
        const email = row.email
        return (
          <Box minH="40px">
            <Tooltip label={value ? t('common.copy') : ''}>
              <Box display="flex" alignItems="center" justifyContent="flex-start">
                <Code
                  as="div"
                  fontSize="sm"
                  bg="transparent"
                  color={tablePrimaryTextColor}
                  cursor={value ? 'pointer' : 'default'}
                  whiteSpace="normal"
                  wordBreak="break-word"
                  _hover={value ? copyableCodeStyles.hover : undefined}
                  _active={value ? copyableCodeStyles.active : undefined}
                  onClick={() => value && copyToClipboard(value, 'Username')}
                >
                  {value ?? t('common.pending')}
                </Code>
              </Box>
            </Tooltip>
            {email && (
              <Tooltip label={t('common.copy')}>
                <Text
                  mt={1}
                  fontSize="xs"
                  color={textColor}
                  whiteSpace="normal"
                  overflowWrap="anywhere"
                  wordBreak="break-word"
                  title={email}
                  cursor="pointer"
                  onClick={() => copyToClipboard(email, 'Email')}
                >
                  {email}
                </Text>
              </Tooltip>
            )}
          </Box>
        )
      },
    },
    {
      key: 'is_active',
      title: t('users.table.status'),
      width: '9%',
      render: (value) => (
        <StatusBadge statusType="user" status={value} label={getStatusText(value)} />
      ),
    },
    {
      key: 'created_at',
      title: t('users.table.registration'),
      width: '10%',
      render: (value) => (
        <Text fontSize="xs" color={textColor}>
          {value ? formatDateTime(value) : t('common.na')}
        </Text>
      ),
    },
    {
      key: 'last_login_at',
      title: t('users.table.lastLogin'),
      width: '10%',
      render: (value) => (
        <Text fontSize="xs" color={textColor}>
          {value ? formatDateTime(value) : t('common.never')}
        </Text>
      ),
    },
    {
      key: 'currentPlan',
      title: t('users.table.plan'),
      width: '10%',
      render: (_value, row) => {
        const planLabel =
          row.currentPlan?.displayName ||
          row.current_plan_display_name ||
          row.currentPlan?.name ||
          row.current_plan_name ||
          row.currentSubscription?.planId ||
          row.current_subscription_plan_id ||
          t('common.na')
        return (
          <Badge colorScheme="purple" fontSize="xs" px={2} py={1}>
            {planLabel}
          </Badge>
        )
      },
    },
    {
      key: 'is_mfa_enabled',
      title: t('users.table.mfa'),
      width: '7%',
      render: (value) => (
        value ? (
          <Badge colorScheme="blue" size="sm">{t('users.mfa.on')}</Badge>
        ) : (
          <Badge colorScheme="gray" size="sm">{t('users.mfa.off')}</Badge>
        )
      ),
    },
  ]

  // Define filter configuration for DataTable
  const filters: FilterConfig[] = [
    {
      key: 'search',
      type: 'text',
      label: t('users.searchPlaceholder'),
      placeholder: t('users.searchPlaceholder'),
      width: '300px',
    },
    {
      key: 'status',
      type: 'select',
      label: t('users.filterByStatus'),
      placeholder: t('users.filterByStatus'),
      width: '200px',
      options: [
        { value: 'true', label: t('users.status.active') },
        { value: 'false', label: t('users.status.inactive') },
      ],
    },
  ]

  // Define pagination configuration
  const paginationConfig: PaginationConfig = {
    page,
    pageSize: limit,
    total: data?.pagination?.total || 0,
    pageSizeOptions: [10, 20, 50, 100],
  }

  // Get actions for each row
  const getRowActions = (user: any): ActionButton[] => {
    return [
      {
        label: '',
        icon: <FiEye />,
        tooltip: t('common.view'),
        onClick: () => handleViewUser(user),
        size: 'xs',
        variant: 'ghost',
      },
      {
        label: '',
        icon: <FiEdit />,
        tooltip: t('common.edit'),
        onClick: () => handleEditUser(user),
        size: 'xs',
        variant: 'ghost',
      },
      ...(user.is_mfa_enabled
        ? [{
            label: '',
            icon: <FiShield />,
            tooltip: t('users.mfaReset.action', 'Reset MFA'),
            onClick: () => handleResetMfa(user),
            size: 'xs' as const,
            variant: 'ghost' as const,
            colorScheme: 'red',
          }]
        : []),
      {
        label: '',
        icon: <FiToggleLeft />,
        tooltip: user.is_active ? t('common.disable') : t('common.enable'),
        onClick: () => handleToggleStatus(user),
        size: 'xs',
        variant: 'ghost',
      },
    ]
  }

  // Handle filter changes
  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setSearch(newFilters.search || '')
    setStatus(newFilters.status || '')
    setPage(1)
  }

  // Handle pagination changes
  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (newSize: number) => {
    setLimit(newSize)
    setPage(1)
  }

  // Handle export
  const handleExport = async () => {
    try {
      const { blob, filename } = await userService.exportCsv({
        search,
        status,
      }, currentEnv)
      triggerBrowserDownload(blob, filename)
      toast.success({
        title: t('common.exportCsv'),
        description: t('common.export.downloaded'),
      })
    } catch (error) {
      toast.error({
        title: t('common.exportCsv'),
        description:
          error instanceof Error ? error.message : t('common.operationFailed'),
      })
    }
  }

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <IdLookupPanel entity="user" />
        <HStack justify="space-between" align="flex-start">
          <Box>
            <Heading size="lg" mb={2}>
              {t('users.title')}
            </Heading>
            <Text color={textColor}>
              {t('users.description')}
            </Text>
          </Box>
          <RefreshIconButton
            label={t('common.refresh')}
            tooltipPlacement="left"
            size="md"
            onClick={() => refetch()}
            isLoading={isFetching}
            isDisabled={isFetching}
          />
        </HStack>

        {/* DataTable with Users */}
        <Box>
          {/* Action Buttons */}
          <Box mb={4}>
            <HStack spacing={3}>
              <Button colorScheme="green">
                {t('users.addUser')}
              </Button>
            </HStack>
          </Box>

          <DataTable<any>
            data={data?.data || []}
            loading={isLoading}
            error={error}
            columns={columns}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            showFilterBar={true}
            sortable={true}
            pagination={paginationConfig}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            actions={getRowActions}
            actionsColumnWidth="8%"
            size="md"
            emptyMessage={t('users.noUsers')}
            showExport={true}
            onExport={handleExport}
            mobileBreakpoint="lg"
          />
        </Box>
      </VStack>

      <Modal isOpen={viewDisclosure.isOpen} onClose={viewDisclosure.onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('users.viewUser')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Tabs
              variant="enclosed"
              colorScheme="admin"
              index={detailTabIndex}
              onChange={setDetailTabIndex}
            >
              <HStack justify="space-between" align="center" mb={4}>
                <TabList mb={0}>
                  <Tab aria-label={t('users.tabs.basicInfo')}>
                    <Tooltip label={t('users.tabs.basicInfo')} hasArrow>
                      <Box display="inline-flex"><FiInfo aria-hidden="true" /></Box>
                    </Tooltip>
                  </Tab>
                  <Tab aria-label={t('users.tabs.plan', 'Plan')}>
                    <Tooltip label={t('users.tabs.plan', 'Plan')} hasArrow>
                      <Box display="inline-flex"><FiCreditCard aria-hidden="true" /></Box>
                    </Tooltip>
                  </Tab>
                  <Tab aria-label={t('users.tabs.orderDefaults')}>
                    <Tooltip label={t('users.tabs.orderDefaults')} hasArrow>
                      <Box display="inline-flex"><FiSliders aria-hidden="true" /></Box>
                    </Tooltip>
                  </Tab>
                  <Tab aria-label={t('users.tabs.loginHistory')}>
                    <Tooltip label={t('users.tabs.loginHistory')} hasArrow>
                      <Box display="inline-flex"><FiClock aria-hidden="true" /></Box>
                    </Tooltip>
                  </Tab>
                </TabList>
                <RefreshIconButton
                  label={t('common.refresh')}
                  tooltipPlacement="left"
                  onClick={() => void handleRefreshCurrentDetailTab()}
                  isLoading={isCurrentDetailTabRefreshing}
                  isDisabled={isCurrentDetailTabRefreshing}
                />
              </HStack>
              <TabPanels>
                <TabPanel px={0} py={4}>
                  <Stack spacing={4} fontSize="sm">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Box>
                        <Text fontSize="xs" color={textColor} mb={1}>{t('users.table.email')}</Text>
                        <Text>{displayedUser?.email ?? t('common.na')}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={textColor} mb={1}>{t('users.table.username')}</Text>
                        <Text>{displayedUser?.username ?? t('common.pending')}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={textColor} mb={1}>{t('users.table.userId')}</Text>
                        <Text fontFamily="mono" wordBreak="break-all">{displayedUser?.id ?? t('common.na')}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={textColor} mb={1}>{t('users.table.status')}</Text>
                        <Text>{getStatusText(displayedUser?.is_active)}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={textColor} mb={1}>{t('users.table.mfa')}</Text>
                        <Text>{displayedUser?.is_mfa_enabled ? t('users.mfa.on') : t('users.mfa.off')}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={textColor} mb={1}>{t('users.emailVerified')}</Text>
                        <Text>{displayedUser?.is_email_verified ? t('common.yes') : t('common.no')}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={textColor} mb={1}>{t('users.table.registration')}</Text>
                        <Text>{displayedUser?.created_at ? formatDateTime(displayedUser.created_at) : t('common.na')}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={textColor} mb={1}>{t('users.table.lastLogin')}</Text>
                        <Text>{displayedUser?.last_login_at ? formatDateTime(displayedUser.last_login_at) : t('common.never')}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={textColor} mb={1}>{t('users.lastLoginIp')}</Text>
                        <Text>{displayedUser?.last_login_ip ?? t('common.na')}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={textColor} mb={1}>{t('users.loginAttempts')}</Text>
                        <Text>{displayedUser?.login_attempts ?? 0}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={textColor} mb={1}>{t('users.lockedUntil')}</Text>
                        <Text>{displayedUser?.locked_until ? formatDateTime(displayedUser.locked_until) : t('common.na')}</Text>
                      </Box>
                    </SimpleGrid>
                  </Stack>
                </TabPanel>
                <TabPanel px={0} py={4}>
                  <Stack spacing={4} fontSize="sm">
                    <HStack justify="flex-end" align="center">
                      <Button size="sm" leftIcon={<FiEdit />} onClick={openChangePlanModal}>
                        {t('users.planChange.button', 'Change Plan')}
                      </Button>
                    </HStack>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Box>
                        <Text fontSize="xs" color={textColor} mb={1}>{t('users.currentPlan')}</Text>
                        <Text>
                          {isSelectedUserDetailLoading && !selectedUserDetail
                            ? t('common.loading')
                            : displayedUser?.currentPlan?.displayName ?? displayedUser?.currentPlan?.name ?? t('common.na')}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={textColor} mb={1}>{t('users.currentPlanPriorityLevel')}</Text>
                        <Text>
                          {isSelectedUserDetailLoading && !selectedUserDetail
                            ? t('common.loading')
                            : displayedUser?.currentPlan?.priorityLevel ?? t('common.na')}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={textColor} mb={1}>{t('users.currentSubscriptionPeriod')}</Text>
                        <Text>
                          {displayedUser?.currentSubscription?.currentPeriodStart
                            ? formatDateTime(displayedUser.currentSubscription.currentPeriodStart)
                            : t('common.na')}
                          {' - '}
                          {displayedUser?.currentSubscription?.currentPeriodEnd
                            ? formatDateTime(displayedUser.currentSubscription.currentPeriodEnd)
                            : t('common.na')}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={textColor} mb={1}>{t('users.planSourceType', 'Source type')}</Text>
                        <Text>{displayedUser?.currentSubscription?.sourceType ?? t('common.na')}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={textColor} mb={1}>{t('users.planSourceRef', 'Source ref')}</Text>
                        <Tooltip
                          label={displayedUser?.currentSubscription?.sourceRef || ''}
                          isDisabled={!displayedUser?.currentSubscription?.sourceRef}
                        >
                          <Text wordBreak="break-word">
                            {displayedUser?.currentSubscription?.sourceRefContent
                              ?? displayedUser?.currentSubscription?.sourceRef
                              ?? t('common.na')}
                          </Text>
                        </Tooltip>
                      </Box>
                    </SimpleGrid>
                    <Box>
                      <Text mb={1}>
                        <strong>{t('users.currentPlanPolicy')}:</strong>
                      </Text>
                      <Code display="block" whiteSpace="pre-wrap" wordBreak="break-word" p={3} fontSize="2xs">
                        {isSelectedUserDetailLoading && !selectedUserDetail
                          ? t('common.loading')
                          : displayedUser?.currentPlan?.merchantConfigPolicyJson ?? t('common.na')}
                      </Code>
                    </Box>
                    {changePlanResult && (
                      <Box>
                        <Alert
                          status={changePlanResult.failedMerchantIds.length > 0 ? 'warning' : 'success'}
                        >
                          <AlertIcon />
                          <Box>
                            <Text>
                              {changePlanResult.rebuiltCount}/{changePlanResult.merchantCount}{' '}
                              {t('users.snapshotImpact.rebuilt', 'merchant snapshots rebuilt')}
                            </Text>
                            {changePlanResult.failedMerchantIds.length > 0 && (
                              <Text fontFamily="mono" wordBreak="break-all">
                                {changePlanResult.failedMerchantIds.join(', ')}
                              </Text>
                            )}
                          </Box>
                        </Alert>
                      </Box>
                    )}
                  </Stack>
                </TabPanel>
                <TabPanel px={0} py={4}>
                  <VStack align="stretch" spacing={4}>
                    <HStack justify="space-between" align="center">
                      <Box>
                        <Text fontWeight="semibold">
                          {t('users.orderDefaults.title')}
                        </Text>
                        <Text fontSize="sm" color={textColor}>
                          {t(
                            'users.orderDefaults.description'
                          )}
                        </Text>
                      </Box>
                      <HStack spacing={2} justify="flex-end" flexWrap="wrap">
                        <RefreshIconButton
                          label={t('users.orderDefaults.refresh')}
                          onClick={handleRefreshUserOrderDefaults}
                          isLoading={refreshUserOrderDefaultsMutation.isPending}
                          isDisabled={isUserSnapshotOperationPending}
                        />
                        <Button
                          leftIcon={recomputeUserSnapshotsMutation.isPending ? <Spinner size="sm" /> : <FiRefreshCw />}
                          onClick={handleRecomputeUserSnapshots}
                          isLoading={recomputeUserSnapshotsMutation.isPending}
                          isDisabled={isUserSnapshotOperationPending}
                          loadingText={t('users.orderDefaults.recomputing')}
                          h="auto"
                          minH="40px"
                          py={2}
                          px={4}
                          fontSize="sm"
                          lineHeight="short"
                          whiteSpace="normal"
                          textAlign="center"
                          colorScheme="blue"
                          variant="outline"
                        >
                          {t('users.orderDefaults.recompute')}
                        </Button>
                      </HStack>
                    </HStack>

                    {userOrderDefaultsRefreshResult ? (
                      <VStack align="stretch" spacing={4}>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                          <Box borderWidth="1px" borderRadius="md" p={3}>
                            <Text fontSize="xs" color={textColor} mb={1}>
                              {t('users.orderDefaults.total')}
                            </Text>
                            <Text fontWeight="semibold">
                              {userOrderDefaultsRefreshResult.total}
                            </Text>
                          </Box>
                          <Box borderWidth="1px" borderRadius="md" p={3}>
                            <Text fontSize="xs" color={textColor} mb={1}>
                              {t('users.orderDefaults.successCount')}
                            </Text>
                            <Text fontWeight="semibold" color="green.500">
                              {userOrderDefaultsRefreshResult.successCount}
                            </Text>
                          </Box>
                          <Box borderWidth="1px" borderRadius="md" p={3}>
                            <Text fontSize="xs" color={textColor} mb={1}>
                              {t('users.orderDefaults.failedCount')}
                            </Text>
                            <Text fontWeight="semibold" color="red.500">
                              {userOrderDefaultsRefreshResult.failedCount}
                            </Text>
                          </Box>
                        </SimpleGrid>

                        {userOrderDefaultsRefreshResult.items.length === 0 ? (
                          <Alert status="info" borderRadius="md">
                            <AlertIcon />
                            {t(
                              'users.orderDefaults.noMerchants'
                            )}
                          </Alert>
                        ) : (
                          <VStack align="stretch" spacing={3}>
                            {userOrderDefaultsRefreshResult.items.map((item, index) => (
                              <Box key={`${item.merchantId}-${index}`} borderWidth="1px" borderRadius="md" p={3}>
                                <HStack justify="space-between" align="start">
                                  <VStack align="start" spacing={1}>
                                    <Text fontWeight="semibold" fontFamily="mono" fontSize="sm">
                                      {item.merchantId}
                                    </Text>
                                    {item.success ? (
                                      <Text fontSize="sm" color="green.500">
                                        {t('users.orderDefaults.refreshItemSuccess')}
                                      </Text>
                                    ) : (
                                      <Text fontSize="sm" color="red.500">
                                        {item.error?.message ?? t('common.operationFailed')}
                                      </Text>
                                    )}
                                  </VStack>
                                  <Badge colorScheme={item.success ? 'green' : 'red'}>
                                    {item.success
                                      ? t('common.success')
                                      : t('common.failed')}
                                  </Badge>
                                </HStack>
                                {item.success && item.snapshotId ? (
                                  <>
                                    <Divider my={3} />
                                    <Text fontSize="xs" color={textColor}>
                                      {t('users.orderDefaults.snapshotId')}:
                                    </Text>
                                    <Code fontSize="xs" whiteSpace="normal" wordBreak="break-all">
                                      {item.snapshotId}
                                    </Code>
                                  </>
                                ) : null}
                              </Box>
                            ))}
                          </VStack>
                        )}
                      </VStack>
                    ) : null}
                  </VStack>
                </TabPanel>
                <TabPanel px={0} py={4}>
                  <DataTable<any>
                    data={loginHistoryData?.data ?? []}
                    loading={isLoginHistoryLoading}
                    error={loginHistoryError as Error | null}
                    columns={[
                      {
                        key: 'loggedInAt',
                        title: t('users.loginHistory.time'),
                        render: (value: string) => (
                          <VStack align="start" spacing={0} fontSize="2xs" lineHeight="short">
                            <Text>{formatDate(value)}</Text>
                            <Text color={textColor}>{formatTime(value)}</Text>
                          </VStack>
                        ),
                      },
                      { key: 'provider', title: t('users.loginHistory.provider') },
                      {
                        key: 'ipAddress',
                        title: t('users.loginHistory.ip'),
                        render: (value: string | null) => value ?? t('common.na'),
                      },
                      {
                        key: 'countryName',
                        title: t('users.loginHistory.country'),
                        render: (value: string | null, row: any) => (
                          <VStack align="start" spacing={0} fontSize="xs" lineHeight="short">
                            <Text>{value ?? t('common.na')}</Text>
                            <Text color={textColor} fontSize="2xs">
                              {row.countryCode ?? t('common.na')}
                            </Text>
                          </VStack>
                        ),
                      },
                    ]}
                    emptyMessage={t('users.loginHistory.empty')}
                    pagination={{
                      page: loginHistoryPage,
                      pageSize: loginHistoryLimit,
                      total: loginHistoryData?.pagination?.total ?? 0,
                      pageSizeOptions: [20, 50, 100],
                    }}
                    onPageChange={setLoginHistoryPage}
                    onPageSizeChange={(newSize) => {
                      setLoginHistoryLimit(newSize)
                      setLoginHistoryPage(1)
                    }}
                    size="sm"
                    mobileBreakpoint="lg"
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={changePlanDisclosure.isOpen} onClose={changePlanDisclosure.onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('users.planChange.title', 'Change Plan')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t('users.currentPlan')}</FormLabel>
                <DropdownSelect
                  value={changePlanForm.planId}
                  options={changePlanSelectOptions}
                  placeholder={t('common.select', 'Select')}
                  size="sm"
                  onChange={(nextPlanId) => {
                    setChangePlanForm({
                      ...changePlanForm,
                      planId: nextPlanId,
                      endMode: 'date',
                    })
                  }}
                />
              </FormControl>
              {String(
                subscriptionPlans.find((plan: any) => plan.id === changePlanForm.planId)?.name ?? ''
              ).toLowerCase() !== 'free' && (
                <>
                  <FormControl>
                    <FormLabel>{t('users.planChange.endMode', 'End')}</FormLabel>
                    <DropdownSelect
                      value={changePlanForm.endMode}
                      options={changePlanEndModeOptions}
                      size="sm"
                      onChange={(value) =>
                        setChangePlanForm({ ...changePlanForm, endMode: value })
                      }
                    />
                  </FormControl>
                  {changePlanForm.endMode === 'date' && (
                    <FormControl>
                      <FormLabel>{t('users.planChange.endsAt', 'Ends at')}</FormLabel>
                      <Input
                        type="datetime-local"
                        value={changePlanForm.endsAt}
                        onChange={(event) =>
                          setChangePlanForm({ ...changePlanForm, endsAt: event.target.value })
                        }
                      />
                    </FormControl>
                  )}
                  {changePlanForm.endMode === 'days' && (
                    <FormControl>
                      <FormLabel>{t('users.planChange.days', 'Days')}</FormLabel>
                      <Input
                        type="number"
                        min={1}
                        value={changePlanForm.durationDays}
                        onChange={(event) =>
                          setChangePlanForm({ ...changePlanForm, durationDays: event.target.value })
                        }
                      />
                    </FormControl>
                  )}
                </>
              )}
              <FormControl isRequired>
                <FormLabel>{t('users.planChange.reason', 'Reason')}</FormLabel>
                <Textarea
                  value={changePlanForm.reason}
                  onChange={(event) =>
                    setChangePlanForm({ ...changePlanForm, reason: event.target.value })
                  }
                />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={changePlanDisclosure.onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleChangePlanSubmit}
                isLoading={changePlanMutation.isPending}
                isDisabled={!changePlanForm.planId || !changePlanForm.reason.trim()}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={editDisclosure.isOpen} onClose={editDisclosure.onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('users.editUser', 'Edit User')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel>{t('users.table.email')}</FormLabel>
                <Input
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder={t('users.table.email')}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t('users.table.username')}</FormLabel>
                <Input
                  value={editForm.username}
                  onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                  placeholder={t('users.table.username')}
                />
              </FormControl>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb="0">{t('users.emailVerified', 'Email verified')}</FormLabel>
                <Switch
                  isChecked={editForm.is_email_verified}
                  onChange={(e) => setEditForm({ ...editForm, is_email_verified: e.target.checked })}
                />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={editDisclosure.onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="blue"
                onClick={submitEdit}
                isLoading={updatePlatformUserMutation.isPending}
                isDisabled={!editUser}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <PlatformUserMfaResetModal
        isOpen={mfaResetDisclosure.isOpen}
        onClose={() => {
          setMfaResetUser(null)
          mfaResetDisclosure.onClose()
        }}
        user={mfaResetUser}
        onSuccess={() => {
          setMfaResetUser(null)
          refetch()
        }}
      />

      <Modal isOpen={statusDisclosure.isOpen} onClose={statusDisclosure.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {statusUser?.is_active
              ? t('users.confirmDisableTitle', 'Disable user')
              : t('users.confirmEnableTitle', 'Enable user')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {statusUser?.is_active
              ? t('users.confirmDisableMessage', 'Are you sure you want to disable this user?')
              : t('users.confirmEnableMessage', 'Enable this user?')}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={statusDisclosure.onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme={statusUser?.is_active ? 'red' : 'green'}
                onClick={() => confirmStatusChange(statusUser)}
                isLoading={updatePlatformUserStatusMutation.isPending}
                isDisabled={!statusUser}
              >
                {statusUser?.is_active ? t('common.disable') : t('common.enable')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
